/**
 * Reading a whole number off the wire without quietly changing it.
 *
 * # One function, because this console had two rules and no way to tell
 *
 * Before #207 there were thirty-six copies of this check across `src/lib`, and
 * they did not agree. Thirty-one used `Number.isInteger`; five and the rest of
 * the console used `Number.isSafeInteger`. Nothing at any call site said which
 * one you had got, and the two differ exactly where it costs the most.
 *
 * # The difference, measured
 *
 * `JSON.parse` produces an IEEE-754 double for a Go `int64`, so a value past
 * 2^53 has already lost digits before any guard here runs:
 *
 *     JSON.parse('{"id": 9007199254740993}').id    →  9007199254740992
 *     JSON.parse('{"id": 9223372036854775807}').id →  9223372036854776000
 *
 * `Number.isInteger` answers `true` to both. The second is the one worth
 * looking at twice: it is `int64` max, it renders as a nineteen-digit number
 * wrong in its **last four digits**, and it is entirely plausible on screen.
 *
 * # Why "close enough" is never enough for any caller of this
 *
 * Every value read through here is an **id, a count, a version or a depth**.
 * Not one is an amount — amounts go through `lib/money`, which refuses this
 * class already and for the same reason.
 *
 *   - A COUNT wrong in its last places reads as a precise figure and is not
 *     one. An operator quotes it.
 *   - A VERSION wrong in its last places names a different version of a rule
 *     that governs money.
 *   - An ID wrong in its last places is a **different record**, and on this
 *     console an id is not only rendered. It is a path segment. `rf-payouts`
 *     put one in `POST /referral/payouts/{id}/approve`; `ac-break` puts one in
 *     `POST /break-glass/{id}/acknowledge`. The id renders as a copyable cell
 *     directly beside the control that consumes it, and `candidates.ts` said
 *     what happens next first: *"the next thing that happens to a copied id is
 *     a promotion or a dismissal against it"*.
 *
 * # The failure mode this replaces is silent in both directions
 *
 * It does not throw and it does not render wrong. A rounded id looks like an
 * id, and the request built from it is a well-formed request addressed at
 * something else. The only reason the payout case was caught is that a test
 * asserted a property somebody believed was already true and the suite
 * disagreed (#202).
 *
 * # `null` is the answer, and callers already know what to do with it
 *
 * Every adapter in this console already renders an unreadable figure as
 * unreadable, carrying the reason — never as a zero, never as a dash with no
 * explanation. This returns `null` into that existing path rather than
 * inventing a new one.
 */
export function exactInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) ? value : null
}

/**
 * The same, refusing a negative.
 *
 * Separate rather than a parameter, so a call site says which rule it wants and
 * a reviewer can see it without opening this file. The callers are counts and
 * ids, where a negative is not a smaller number — it is a value the backend
 * cannot have meant, and clamping it to zero would report "none" for a figure
 * that was never read.
 */
export function exactCount(value: unknown): number | null {
  const n = exactInteger(value)
  return n === null || n < 0 ? null : n
}

/**
 * The same, refusing zero as well.
 *
 * For the values where zero is not a member of the set rather than merely
 * unwelcome: a database id, a page limit, a version number. `0` from any of
 * those is a row that does not exist or a page with nothing on it, and both are
 * better read as "we could not state this" than acted on.
 */
export function exactPositive(value: unknown): number | null {
  const n = exactInteger(value)
  return n === null || n <= 0 ? null : n
}
